import React from 'react';
import PropTypes from 'prop-types';
import { Wallet, Plus, Trash2 } from 'lucide-react';
import {
  FormInput,
  CurrencyInput,
  FormSelect,
  SectionHeader,
  FormNavigation,
  FormRow,
} from '../FormComponents';

const assetTypeOptions = [
  { value: 'checking', label: 'Checking Account' },
  { value: 'savings', label: 'Savings Account' },
  { value: 'money_market', label: 'Money Market Account' },
  { value: 'cd', label: 'Certificate of Deposit (CD)' },
  { value: '401k', label: '401(k)' },
  { value: 'ira', label: 'IRA' },
  { value: 'roth_ira', label: 'Roth IRA' },
  { value: 'stocks', label: 'Stocks/Bonds' },
  { value: 'mutual_funds', label: 'Mutual Funds' },
  { value: 'gift', label: 'Gift Funds' },
  { value: 'trust', label: 'Trust Account' },
  { value: 'real_estate', label: 'Real Estate Equity' },
  { value: 'business', label: 'Business Equity' },
  { value: 'other', label: 'Other Assets' },
];

export default function Assets({ data, onChange, onNext, onBack }) {
  const assets = data.assets || [];

  const addAsset = () => {
    onChange({
      ...data,
      assets: [...assets, { id: Date.now(), type: '', institution: '', accountNumber: '', balance: '' }],
    });
  };

  const updateAsset = (id, field, value) => {
    onChange({
      ...data,
      assets: assets.map((asset) =>
        asset.id === id ? { ...asset, [field]: value } : asset
      ),
    });
  };

  const removeAsset = (id) => {
    onChange({
      ...data,
      assets: assets.filter((asset) => asset.id !== id),
    });
  };

  const totalAssets = assets.reduce(
    (sum, asset) => sum + (parseFloat(asset.balance) || 0),
    0
  );

  // Allow skipping assets section
  const isValid = true;

  return (
    <div>
      <SectionHeader
        title="Assets"
        subtitle="List your savings, investments, and other assets"
        icon={<Wallet size={28} />}
      />

      <div
        style={{
          padding: '16px 20px',
          borderRadius: '12px',
          background: 'linear-gradient(135deg, rgba(56, 189, 248, 0.1), rgba(99, 102, 241, 0.05))',
          border: '1px solid rgba(56, 189, 248, 0.2)',
          marginBottom: '24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <span style={{ color: '#94a3b8', fontSize: '14px' }}>Total Assets</span>
        <span
          style={{
            fontSize: '24px',
            fontWeight: 700,
            color: '#38bdf8',
          }}
        >
          {new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
          }).format(totalAssets)}
        </span>
      </div>

      {assets.length === 0 && (
        <div
          style={{
            padding: '32px',
            borderRadius: '12px',
            border: '2px dashed rgba(148, 163, 184, 0.3)',
            textAlign: 'center',
            marginBottom: '20px',
          }}
        >
          <p style={{ color: '#94a3b8', margin: '0 0 8px' }}>
            No assets added yet.
          </p>
          <p style={{ color: '#64748b', margin: 0, fontSize: '13px' }}>
            Adding assets helps demonstrate your financial stability.
          </p>
        </div>
      )}

      {assets.map((asset, index) => (
        <div
          key={asset.id}
          style={{
            padding: '20px',
            borderRadius: '12px',
            background: 'rgba(255, 255, 255, 0.03)',
            border: '1px solid rgba(148, 163, 184, 0.2)',
            marginBottom: '16px',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '16px',
            }}
          >
            <span
              style={{
                fontSize: '13px',
                fontWeight: 600,
                color: '#64748b',
              }}
            >
              Asset #{index + 1}
            </span>
            <button
              type="button"
              onClick={() => removeAsset(asset.id)}
              style={{
                background: 'none',
                border: 'none',
                color: '#f87171',
                cursor: 'pointer',
                padding: '4px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                fontSize: '12px',
              }}
            >
              <Trash2 size={14} /> Remove
            </button>
          </div>

          <FormRow>
            <FormSelect
              label="Asset Type"
              value={asset.type}
              onChange={(value) => updateAsset(asset.id, 'type', value)}
              options={assetTypeOptions}
              placeholder="Select type"
              required
            />
            <FormInput
              label="Financial Institution"
              value={asset.institution}
              onChange={(value) => updateAsset(asset.id, 'institution', value)}
              placeholder="Bank of America"
            />
          </FormRow>

          <FormRow>
            <FormInput
              label="Account Number (last 4 digits)"
              value={asset.accountNumber}
              onChange={(value) => updateAsset(asset.id, 'accountNumber', value.replace(/\D/g, '').slice(0, 4))}
              placeholder="1234"
              maxLength={4}
              inputMode="numeric"
            />
            <CurrencyInput
              label="Current Balance"
              value={asset.balance}
              onChange={(value) => updateAsset(asset.id, 'balance', value)}
              required
              placeholder="$0"
            />
          </FormRow>
        </div>
      ))}

      <button
        type="button"
        onClick={addAsset}
        style={{
          width: '100%',
          padding: '14px',
          borderRadius: '12px',
          border: '2px dashed rgba(56, 189, 248, 0.4)',
          background: 'rgba(56, 189, 248, 0.05)',
          color: '#38bdf8',
          fontSize: '14px',
          fontWeight: 600,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          transition: 'all 0.2s ease',
          marginBottom: '8px',
        }}
      >
        <Plus size={18} /> Add Asset
      </button>

      <p style={{ fontSize: '12px', color: '#64748b', textAlign: 'center', marginBottom: '24px' }}>
        Include all accounts you plan to use for down payment and closing costs.
      </p>

      <FormNavigation
        onBack={onBack}
        onNext={onNext}
        onSkip={assets.length === 0 ? onNext : undefined}
        isValid={isValid}
      />
    </div>
  );
}

Assets.propTypes = {
  data: PropTypes.shape({
    assets: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        type: PropTypes.string,
        institution: PropTypes.string,
        accountNumber: PropTypes.string,
        balance: PropTypes.string,
      })
    ),
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
};
